import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';
import { Product, Menu } from 'public-contracts';

export interface ProductListResponse {
  success: boolean;
  data: Product[];
  total?: number;
}

export interface ProductResponse {
  success: boolean;
  data: Product;
  message?: string;
}

export interface ProductCostCalculation {
  productId: string;
  productName?: string;
  baseCost: number;
  currency: string;
  suggestedPrice: number;
  markupPercentage: number;
  calculatedAt: string;
  recipeCost?: {
    recipeId: string;
    recipeName?: string;
    totalCost: number;
    breakdown: Array<{
      ingredientId: string;
      ingredientName?: string;
      amount: number;
      unit: string;
      inventoryId: string;
      inventoryName?: string;
      unitPrice: number;
      totalCost: number;
    }>;
  };
}

export interface ProductCostResponse {
  success: boolean;
  data: ProductCostCalculation;
}

class ProductsService {
  private apiRoot = configs.apiRoot;

  async getProducts(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
    limit?: number;
    q?: string;
  }): Promise<Product[] & { __contentRange?: string; __total?: number }> {
    const queryParams = new URLSearchParams();

    // CRITICAL: API requires BOTH page AND size to be present!
    // If only size is provided, it defaults to size=10
    const page = params?.page ?? 1;
    let size = 10; // Default size

    // Determine size from various parameter names
    if (params?.size) {
      size = params.size;
    } else if (params?.perPage) {
      size = params.perPage;
    } else if (params?.limit) {
      size = params.limit;
    }

    // Always set both page and size
    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());

    if (params?.q) queryParams.append('q', params.q);
    if (params?.filter)
      queryParams.append('filter', JSON.stringify(params.filter));
    if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

    const url = `${this.apiRoot}/products${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    console.log(`🔍 Fetching products from: ${url}`);
    console.log(`📊 Request params: page=${page}, size=${size}`);

    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch products: ${response.statusText}`
      );
    }

    const result = await response.json();
    console.log(
      `📦 API Response type:`,
      Array.isArray(result) ? 'Array' : typeof result
    );
    console.log(
      `📦 API Response length:`,
      Array.isArray(result) ? result.length : result.data?.length || 0
    );

    // Check Content-Range header for total count
    const contentRange = response.headers.get('Content-Range');
    let total: number | undefined;

    if (contentRange) {
      console.log(`📊 Content-Range header:`, contentRange);
      // Format: "products 0-9/100" means items 0-9 out of 100 total
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        total = parseInt(match[1], 10);
        console.log(`📊 Total products available: ${total}`);
      }
    }

    // Handle both array response and object with data property
    let products: Product[] = [];

    if (Array.isArray(result)) {
      console.log(`✅ Returning ${result.length} products as array`);
      products = result;
    } else if (result.data && Array.isArray(result.data)) {
      console.log(
        `✅ Returning ${result.data.length} products from result.data`
      );
      products = result.data;
    } else {
      console.warn(`⚠️ Unexpected response format:`, result);
      products = [];
    }

    // Attach metadata to the array
    (products as any).__contentRange = contentRange;
    (products as any).__total = total;

    return products as Product[] & {
      __contentRange?: string;
      __total?: number;
    };
  }

  async getProduct(id: string): Promise<Product> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/products/${id}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch product: ${response.statusText}`
      );
    }

    const result: ProductResponse = await response.json();
    return result.data || result;
  }

  async createProduct(data: Partial<Product>): Promise<Product> {
    const response = await authenticatedFetch(`${this.apiRoot}/products`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let errorMessage = `Failed to create product`;
      try {
        const error = await response.json();
        errorMessage = error.message || error.error || errorMessage;
        
        // Provide more helpful error messages for common issues
        if (errorMessage.includes('PRODUCT_INVALID')) {
          if (errorMessage.includes('must have either a recipe')) {
            errorMessage = 'Product creation failed: This product needs either a recipe or inventory mapping. Please ensure at least one warehouse exists, or select a recipe for this product.';
          } else if (errorMessage.includes('cannot have both')) {
            errorMessage = 'Product creation failed: A product cannot have both a recipe and direct inventory mapping. Please choose one option.';
          }
        }
      } catch (e) {
        // If JSON parsing fails, use status text
        errorMessage = `Failed to create product: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result: ProductResponse = await response.json();
    return result.data || result;
  }

  async updateProduct(id: string, data: Partial<Product>): Promise<Product> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/products/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to update product: ${response.statusText}`
      );
    }

    const result: ProductResponse = await response.json();
    return result.data || result;
  }

  async deleteProduct(id: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/products/${id}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      let errorMessage = `Failed to delete product: ${response.statusText}`;
      
      // Handle 403 Forbidden specifically
      if (response.status === 403) {
        errorMessage = 'You do not have permission to delete products. Please contact your administrator.';
      }
      
      // Try to extract error message from response
      try {
        const error = await response.json();
        if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
      } catch {
        // If JSON parsing fails, use the status-specific message
      }
      
      throw new Error(errorMessage);
    }
  }

  async getProductCategories(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
  }): Promise<any[] & { __total?: number; __page?: number; __perPage?: number }> {
    try {
      const queryParams = new URLSearchParams();

      // CRITICAL: API requires BOTH page AND size to be present!
      // If only size is provided, it defaults to size=10
      const page = params?.page ?? 1;
      let size = 10; // Default size

      // Determine size from various parameter names
      if (params?.size) {
        size = params.size;
      } else if (params?.perPage) {
        size = params.perPage;
      }

      // Always set both page and size
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());

      if (params?.filter)
        queryParams.append('filter', JSON.stringify(params.filter));
      if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

      const url = `${this.apiRoot}/product_categories${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;
      console.log('Fetching product categories from:', url);
      console.log(`📊 Request params: page=${page}, size=${size}`);

      const response = await authenticatedFetch(url, { method: 'GET' });

      console.log('Product categories response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Product categories API error:', errorText);
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        throw new Error(
          error.message ||
            `Failed to fetch product categories: ${response.statusText}`
        );
      }

      const result = await response.json();
      console.log('Product categories result:', result);
      const categories = Array.isArray(result) ? result : result.data || [];
      
      // Check Content-Range header for total count
      const contentRange = response.headers.get('Content-Range');
      let total = categories.length;
      if (contentRange) {
        const match = contentRange.match(/\/(\d+)$/);
        if (match) {
          total = parseInt(match[1], 10);
        }
      }

      console.log(`✅ Loaded ${categories.length} product categories (total: ${total})`);
      console.log('📋 Category names:', categories.map((c: any) => c.name));
      
      // Check specifically for "Grab - topping"
      const grabTopping = categories.find((c: any) => 
        c.name && (c.name.includes('Grab') && c.name.includes('topping'))
      );
      if (grabTopping) {
        console.log('✅ Found Grab - topping category:', grabTopping);
      } else {
        console.warn('⚠️ Grab - topping category NOT found in response');
        console.log('Available categories:', categories.map((c: any) => ({ id: c.id, name: c.name })));
      }

      // Attach pagination metadata to the array
      const resultWithMeta = categories as any[] & {
        __total?: number;
        __page?: number;
        __perPage?: number;
      };
      resultWithMeta.__total = total;
      resultWithMeta.__page = page;
      resultWithMeta.__perPage = size;

      return resultWithMeta;
    } catch (error) {
      console.error('Error fetching product categories:', error);
      // Return empty array instead of throwing to prevent form from breaking
      return [] as any;
    }
  }

  async getProductCategory(id: string): Promise<any> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/product_categories/${id}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message ||
          `Failed to fetch product category: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data || result;
  }

  async createProductCategory(data: { name: string; parentId?: string }): Promise<any> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/product_categories`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message ||
          `Failed to create product category: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data || result;
  }

  async updateProductCategory(
    id: string,
    data: { name: string; parentId?: string }
  ): Promise<any> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/product_categories/${id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message ||
          `Failed to update product category: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data || result;
  }

  async deleteProductCategory(id: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/product_categories/${id}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message ||
          `Failed to delete product category: ${response.statusText}`
      );
    }
  }

  /**
   * Fetch all products by making multiple requests if needed
   * This handles cases where there are more products than a single page can return
   */
  async getAllProducts(params?: {
    filter?: any;
    sort?: any;
    q?: string;
    maxSize?: number; // Maximum products to fetch (default: 10000)
  }): Promise<Product[]> {
    const maxSize = params?.maxSize ?? 10000;
    const pageSize = 1000; // Fetch 1000 at a time
    let allProducts: Product[] = [];
    let currentPage = 1;
    let hasMore = true;
    let totalFetched = 0;

    console.log(`🔄 Fetching all products (max: ${maxSize})...`);

    while (hasMore && totalFetched < maxSize) {
      const remaining = maxSize - totalFetched;
      const size = Math.min(pageSize, remaining);

      console.log(`📄 Fetching page ${currentPage} with size ${size}...`);

      const products = await this.getProducts({
        page: currentPage,
        size: size,
        filter: params?.filter,
        sort: params?.sort,
        q: params?.q,
      });

      if (products.length === 0) {
        hasMore = false;
        break;
      }

      allProducts = allProducts.concat(products);
      totalFetched += products.length;

      console.log(
        `✅ Page ${currentPage}: ${products.length} products (total: ${totalFetched})`
      );

      // If we got fewer products than requested, we've reached the end
      if (products.length < size) {
        hasMore = false;
        break;
      }

      currentPage++;
    }

    console.log(`✅ Fetched all ${allProducts.length} products`);
    return allProducts;
  }

  /**
   * Calculate product cost and suggested price
   */
  async calculateProductCost(
    productId: string,
    markupPercentage?: number
  ): Promise<ProductCostCalculation> {
    const queryParams = new URLSearchParams();
    if (markupPercentage !== undefined) {
      queryParams.append('markupPercentage', markupPercentage.toString());
    }

    const url = `${this.apiRoot}/products/${productId}/cost${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to calculate product cost: ${response.statusText}`
      );
    }

    const result: ProductCostResponse = await response.json();
    return result.data;
  }

  /**
   * Update product with calculated cost and suggested price
   */
  async updateProductCost(
    productId: string,
    markupPercentage?: number
  ): Promise<Product> {
    const url = `${this.apiRoot}/products/${productId}/calculate-cost`;
    const body: any = {};
    if (markupPercentage !== undefined) {
      body.markupPercentage = markupPercentage;
    }

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message ||
          `Failed to update product cost: ${response.statusText}`
      );
    }

    const result: ProductResponse = await response.json();
    return result.data;
  }

  // Menu methods
  async getMenus(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
  }): Promise<Menu[] & { __contentRange?: string; __total?: number }> {
    const queryParams = new URLSearchParams();
    const page = params?.page ?? 1;
    let size = 10;

    if (params?.size) {
      size = params.size;
    } else if (params?.perPage) {
      size = params.perPage;
    }

    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());

    if (params?.filter)
      queryParams.append('filter', JSON.stringify(params.filter));
    if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

    const url = `${this.apiRoot}/menus${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch menus: ${response.statusText}`
      );
    }

    const contentRange = response.headers.get('content-range');
    const result = await response.json();
    const menus = Array.isArray(result.data) ? result.data : result;

    if (contentRange) {
      const total = parseInt(contentRange.split('/')[1], 10);
      return Object.assign(menus, {
        __contentRange: contentRange,
        __total: total,
      });
    }

    return menus;
  }

  async getMenu(id: string): Promise<Menu> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/menus/${id}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch menu: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data || result;
  }

  async createMenu(data: {
    name: string;
    description?: string;
    categorySelections: Array<{
      categoryId: string;
      excludedProductIds: string[];
    }>;
    isActive?: boolean;
  }): Promise<Menu> {
    const response = await authenticatedFetch(`${this.apiRoot}/menus`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to create menu: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data || result;
  }

  async updateMenu(
    id: string,
    data: {
      name: string;
      description?: string;
      categorySelections: Array<{
        categoryId: string;
        excludedProductIds: string[];
      }>;
      isActive?: boolean;
    }
  ): Promise<Menu> {
    const response = await authenticatedFetch(`${this.apiRoot}/menus/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to update menu: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data || result;
  }

  async deleteMenu(id: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/menus/${id}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to delete menu: ${response.statusText}`
      );
    }
  }
}

export const productsService = new ProductsService();
